/**
 * SAML XML manipulation utilities for the split SAML second signing flow.
 *
 * IMPORTANT: Uses string-level operations to preserve the original XML
 * byte-for-byte. DOMParser/XMLSerializer round-trips are NOT safe for
 * signed XML because the serializer can add namespace declarations,
 * normalize whitespace, or reorder attributes -- any of which breaks
 * the assertion digest the SP verifies.
 */

// Matches the SignatureValue element, capturing the opening tag, content, and closing tag.
// Handles both prefixed (ds:SignatureValue) and unprefixed (SignatureValue) forms.
const SIGNATURE_VALUE_RE =
  /(<(?:[\w-]+:)?SignatureValue(?:\s[^>]*)?>)([\s\S]*?)(<\/(?:[\w-]+:)?SignatureValue>)/g;

/**
 * Extract sigma1 bytes from a SAML response XML's assertion SignatureValue.
 * The assertion should have exactly one SignatureValue element.
 */
export function extractSigma1FromXml(samlXml: string): Uint8Array {
  const matches = [...samlXml.matchAll(SIGNATURE_VALUE_RE)];

  if (matches.length === 0) {
    throw new Error('No SignatureValue element found in SAML assertion');
  }

  if (matches.length > 1) {
    throw new Error(
      `Expected 1 SignatureValue, found ${matches.length}. ` +
        'Split SAML should only have assertion-level signing.'
    );
  }

  const sigValueB64 = matches[0][2].trim();

  if (!sigValueB64) {
    throw new Error('SignatureValue element is empty');
  }

  const binaryString = atob(sigValueB64);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
}

/**
 * Replace the assertion's SignatureValue content with a new base64-encoded signature.
 * Uses string replacement to preserve the original XML byte-for-byte.
 */
export function replaceSignatureValueInXml(
  samlXml: string,
  newSignatureB64: string
): string {
  const matches = [...samlXml.matchAll(SIGNATURE_VALUE_RE)];

  if (matches.length !== 1) {
    throw new Error(`Expected 1 SignatureValue, found ${matches.length}`);
  }

  return samlXml.replace(SIGNATURE_VALUE_RE, `$1${newSignatureB64}$3`);
}

/**
 * Create and auto-submit a POST form to the SP's Assertion Consumer Service URL.
 * This is the standard SAML HTTP-POST binding mechanism.
 */
export function autoSubmitToAcs(
  samlResponseB64: string,
  acsUrl: string,
  relayState: string
): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = acsUrl;
  form.style.display = 'none';

  const samlInput = document.createElement('input');
  samlInput.type = 'hidden';
  samlInput.name = 'SAMLResponse';
  samlInput.value = samlResponseB64;
  form.appendChild(samlInput);

  if (relayState) {
    const relayInput = document.createElement('input');
    relayInput.type = 'hidden';
    relayInput.name = 'RelayState';
    relayInput.value = relayState;
    form.appendChild(relayInput);
  }

  document.body.appendChild(form);
  form.submit();
}
